import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, ShieldCheck, Users, BookOpen, ClipboardList,
  Handshake, Layers, HeartHandshake, Building2, ScrollText,
} from 'lucide-react';
import HussarTopNav from '../components/HussarTopNav';

/**
 * DlaProfesjonalistow — v4.0 Phase 4 (fleshed).
 *
 * Full audience surface for law firms, NGOs, universities, relocation
 * advisors and HR/legal teams that help people prepare immigration
 * records. Renders locked hero copy, five deep sections
 * (for-helpers, what-it-prepares, boundaries, who-it-helps,
 * why-structured), and the shared safety doctrine.
 *
 * No backend calls. Public route.
 */
const PAGE_BG = 'var(--hussar-bg-page, #0B1220)';
const TEXT_PRIMARY = 'var(--hussar-text-primary, #E5E7EB)';
const CYAN = '#7DD3FC';
const CYAN_STRONG = '#38BDF8';
const RED_SOFT = 'rgba(248,113,113,0.30)';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
    const name = selector.match(/name="([^"]+)"/)?.[1];
    const prop = selector.match(/property="([^"]+)"/)?.[1];
    const rel = selector.match(/rel="([^"]+)"/)?.[1];
    if (name) el.setAttribute('name', name);
    if (prop) el.setAttribute('property', prop);
    if (rel) el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const cardStyle = {
  background: 'rgba(15,23,42,0.55)',
  border: '1px solid rgba(148,163,184,0.15)',
  borderRadius: 14,
  padding: '26px 26px 22px',
  marginBottom: 22,
};

const iconChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: 8,
  background: 'rgba(56,189,248,0.10)',
  border: '1px solid rgba(56,189,248,0.30)',
  color: CYAN_STRONG,
  marginBottom: 12,
};

const sectionTitleStyle = {
  fontSize: 20,
  fontWeight: 700,
  color: '#F1F5F9',
  margin: '0 0 10px',
  letterSpacing: -0.2,
};

const sectionBodyStyle = {
  fontSize: 15,
  lineHeight: 1.65,
  color: 'rgba(226,232,240,0.86)',
  margin: '0 0 12px',
};

const bulletListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'grid',
  gap: 8,
};

const bulletItemStyle = {
  fontSize: 14,
  lineHeight: 1.55,
  color: 'rgba(226,232,240,0.88)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

const bulletDotStyle = {
  flex: '0 0 auto',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: CYAN,
  marginTop: 8,
};

const DlaProfesjonalistow = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('dlaProfesjonalistow.metaTitle');
    setMeta('meta[name="description"]', 'content', t('dlaProfesjonalistow.metaDescription'));
    setMeta('meta[property="og:title"]', 'content', t('dlaProfesjonalistow.metaTitle'));
    setMeta('meta[property="og:description"]', 'content', t('dlaProfesjonalistow.metaDescription'));
    setMeta('link[rel="canonical"]', 'href', 'https://hussarai.org/dla-profesjonalistow');
  }, [t]);

  const prepareItems = t('dlaProfesjonalistow.whatItPrepares.items', { returnObjects: true }) || [];
  const whoItems = t('dlaProfesjonalistow.whoItHelps.items', { returnObjects: true }) || [];
  const whyItems = t('dlaProfesjonalistow.whyStructured.items', { returnObjects: true }) || [];

  return (
    <main
      data-testid="dla-profesjonalistow-page"
      style={{ minHeight: '100vh', background: PAGE_BG, color: TEXT_PRIMARY, padding: '56px 20px 96px' }}
    >
      <HussarTopNav active="forProfessionals" />
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <Link
          to="/"
          data-testid="dla-profesjonalistow-back-link"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(125,211,252,0.85)', textDecoration: 'none', marginBottom: 28 }}
        >
          <ArrowLeft className="w-4 h-4" /> HussarAi
        </Link>

        {/* Hero */}
        <p data-testid="dla-profesjonalistow-eyebrow" style={{ fontSize: 11, letterSpacing: 1.6, fontWeight: 700, color: CYAN, textTransform: 'uppercase', marginBottom: 14 }}>
          {t('dlaProfesjonalistow.eyebrow')}
        </p>
        <h1 data-testid="dla-profesjonalistow-h1" style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, lineHeight: 1.2, marginBottom: 18, color: '#F1F5F9' }}>
          {t('dlaProfesjonalistow.heroTitle')}
        </h1>
        <p data-testid="dla-profesjonalistow-subtitle" style={{ fontSize: 17, lineHeight: 1.65, color: 'rgba(226,232,240,0.9)', marginBottom: 20 }}>
          {t('dlaProfesjonalistow.heroSubtitle')}
        </p>
        <p data-testid="dla-profesjonalistow-positioning" style={{ fontSize: 15, lineHeight: 1.65, color: 'rgba(226,232,240,0.75)', marginBottom: 24 }}>
          {t('dlaProfesjonalistow.positioning')}
        </p>

        {/* Trust card */}
        <div data-testid="dla-profesjonalistow-trust-card" style={{ padding: '18px 20px', marginBottom: 20, background: 'rgba(248,113,113,0.06)', border: `1px solid ${RED_SOFT}`, borderLeft: '3px solid #F87171', borderRadius: 12 }}>
          <p style={{ fontSize: 12, letterSpacing: 1.2, fontWeight: 700, color: '#F87171', margin: 0, textTransform: 'uppercase' }}>Granica systemu</p>
          <p data-testid="dla-profesjonalistow-trust" style={{ fontSize: 14, lineHeight: 1.6, marginTop: 8, color: 'rgba(226,232,240,0.92)' }}>
            {t('dlaProfesjonalistow.trust')}
          </p>
        </div>

        <p data-testid="dla-profesjonalistow-stub-notice" style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(226,232,240,0.72)', marginBottom: 28 }}>
          {t('dlaProfesjonalistow.stubNotice')}
        </p>

        {/* Top CTA row — primary Moje Akta / secondary Instytucje / tertiary Wiedza */}
        <div data-testid="dla-profesjonalistow-cta-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 44 }}>
          <Link to="/moje-akta-imigracyjne" data-testid="dla-profesjonalistow-cta-moje-akta" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 700, background: CYAN, color: '#0B1220', textDecoration: 'none', fontSize: 15 }}>
            <Users className="w-4 h-4" aria-hidden="true" />
            {t('dlaProfesjonalistow.ctaMojeAkta')}
          </Link>
          <Link to="/dla-instytucji" data-testid="dla-profesjonalistow-cta-dla-instytucji" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 600, background: 'rgba(15,23,42,0.55)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14 }}>
            <Building2 className="w-4 h-4" aria-hidden="true" />
            {t('dlaProfesjonalistow.ctaDlaInstytucji')}
          </Link>
          <Link to="/wiedza" data-testid="dla-profesjonalistow-cta-wiedza" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 600, background: 'transparent', border: '1px solid rgba(148,163,184,0.30)', color: 'rgba(226,232,240,0.85)', textDecoration: 'none', fontSize: 14 }}>
            <BookOpen className="w-4 h-4" aria-hidden="true" />
            {t('dlaProfesjonalistow.ctaWiedza')}
          </Link>
        </div>

        {/* Section 1 — For helpers */}
        <section data-testid="dla-profesjonalistow-for-helpers" style={cardStyle}>
          <span style={iconChipStyle}><HeartHandshake className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-profesjonalistow-for-helpers-title" style={sectionTitleStyle}>{t('dlaProfesjonalistow.forHelpers.title')}</h2>
          <p style={sectionBodyStyle}>{t('dlaProfesjonalistow.forHelpers.body')}</p>
        </section>

        {/* Section 2 — What HussarAi helps prepare */}
        <section data-testid="dla-profesjonalistow-what-it-prepares" style={cardStyle}>
          <span style={iconChipStyle}><ClipboardList className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-profesjonalistow-what-it-prepares-title" style={sectionTitleStyle}>{t('dlaProfesjonalistow.whatItPrepares.title')}</h2>
          <ul style={bulletListStyle} data-testid="dla-profesjonalistow-what-it-prepares-list">
            {prepareItems.map((item) => (
              <li key={item} style={bulletItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 3 — Professional boundaries */}
        <section data-testid="dla-profesjonalistow-boundaries" style={cardStyle}>
          <span style={iconChipStyle}><ShieldCheck className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-profesjonalistow-boundaries-title" style={sectionTitleStyle}>{t('dlaProfesjonalistow.boundaries.title')}</h2>
          <p style={sectionBodyStyle}>{t('dlaProfesjonalistow.boundaries.body')}</p>
        </section>

        {/* Section 4 — Who this helps */}
        <section data-testid="dla-profesjonalistow-who-it-helps" style={cardStyle}>
          <span style={iconChipStyle}><Handshake className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-profesjonalistow-who-it-helps-title" style={sectionTitleStyle}>{t('dlaProfesjonalistow.whoItHelps.title')}</h2>
          <ul style={bulletListStyle} data-testid="dla-profesjonalistow-who-it-helps-list">
            {whoItems.map((item) => (
              <li key={item} style={bulletItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 5 — Why structured records matter */}
        <section data-testid="dla-profesjonalistow-why-structured" style={cardStyle}>
          <span style={iconChipStyle}><Layers className="w-4 h-4" aria-hidden="true" /></span>
          <h2 data-testid="dla-profesjonalistow-why-structured-title" style={sectionTitleStyle}>{t('dlaProfesjonalistow.whyStructured.title')}</h2>
          <ul style={bulletListStyle} data-testid="dla-profesjonalistow-why-structured-list">
            {whyItems.map((item) => (
              <li key={item} style={bulletItemStyle}>
                <span style={bulletDotStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Shared safety strip */}
        <section
          data-testid="dla-profesjonalistow-safety-strip"
          style={{ ...cardStyle, background: 'rgba(30,41,59,0.55)', borderLeft: `3px solid ${CYAN_STRONG}` }}
        >
          <span style={iconChipStyle}><ScrollText className="w-4 h-4" aria-hidden="true" /></span>
          <p data-testid="dla-profesjonalistow-safety-doctrine" style={{ ...sectionBodyStyle, color: 'rgba(226,232,240,0.92)', marginBottom: 10 }}>
            {t('dlaProfesjonalistow.safety.doctrine')}
          </p>
          <p data-testid="dla-profesjonalistow-safety-integrity" style={{ ...sectionBodyStyle, color: 'rgba(226,232,240,0.88)', fontStyle: 'italic', marginBottom: 10 }}>
            {t('dlaProfesjonalistow.safety.integrity')}
          </p>
          <p data-testid="dla-profesjonalistow-safety-not-legal-advice" style={{ ...sectionBodyStyle, color: 'rgba(226,232,240,0.72)', marginBottom: 0, fontSize: 13 }}>
            {t('dlaProfesjonalistow.safety.notLegalAdvice')}
          </p>
        </section>

        <p data-testid="dla-profesjonalistow-doctrine-footer" style={{ marginTop: 36, fontSize: 12, lineHeight: 1.6, color: 'rgba(226,232,240,0.55)', fontStyle: 'italic' }}>
          AI przygotowuje. Człowiek decyduje. Każde działanie zostawia ślad audytowy.
        </p>
      </div>
    </main>
  );
};

export default DlaProfesjonalistow;
